import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();

  const menuItems = [
    { path: '/', icon: 'ri-dashboard-line', label: t('sidebar.home') },
    { path: '/users', icon: 'ri-user-settings-line', label: t('sidebar.users'), children: [
      { path: '/waitlist', icon: 'ri-time-line', label: t('sidebar.waitlist') },
    ]},
    { path: '/resources', icon: 'ri-settings-3-line', label: t('sidebar.resources') },
    { path: '/tenants', icon: 'ri-building-line', label: t('sidebar.tenants') },
    { path: '/plans', icon: 'ri-price-tag-3-line', label: t('sidebar.plans') },
    { path: '/audit', icon: 'ri-file-list-3-line', label: t('sidebar.audit') },
    { path: '/reports', icon: 'ri-bar-chart-box-line', label: t('sidebar.reports') },
    { path: '/settings', icon: 'ri-settings-4-line', label: t('sidebar.settings') }
  ] as { path: string; icon: string; label: string; children?: { path: string; icon: string; label: string }[] }[];

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (item: typeof menuItems[0]) =>
    isActive(item.path) || (item.children?.some(c => isActive(c.path)) ?? false);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        ></div>
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <img
                src="/images/logo.png"
                alt="PandaPanla"
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">PandaPanla</span>
            </Link>
            <button
              onClick={onClose}
              className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <ul className="space-y-2">
              {menuItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      isParentActive(item)
                        ? 'bg-teal-50 text-teal-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => onClose()}
                  >
                    <i className={`${item.icon} text-xl`}></i>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                  {item.children && isParentActive(item) && (
                    <ul className="ml-8 mt-1 space-y-1">
                      {item.children.map(child => (
                        <li key={child.path}>
                          <Link to={child.path}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive(child.path) ? 'bg-teal-50 text-teal-600' : 'text-gray-500 hover:bg-gray-50'
                            }`}
                            onClick={() => onClose()}>
                            <i className={`${child.icon} text-base`}></i>
                            <span>{child.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">{t('sidebar.admin').charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{t('sidebar.admin')}</p>
                <p className="text-xs text-gray-500">admin@pandapanla.com</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
